import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArchive } from "react-icons/io";
import UserService from "../../../../services/UserService";
import { toast } from "react-toastify";
import { colors } from "../../../config/colors";

const ViewEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        const response = await UserService.getUserById(id);
        if (response.data.status === "success") {
          setEmployee(response.data.data.user);
        } else {
          throw new Error("Failed to fetch employee details");
        }
      } catch (error) {
        console.error("Error fetching employee details:", error);
        toast.error("Failed to fetch employee details");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetails();
  }, [id]);

  const handleClose = () => {
    navigate("/employee-list");
  };

  const handleArchiveEmployee = async () => {
    try {
      setArchiving(true);
      const response = await UserService.archiveUser(employee._id, true);
      if (response.data.success) {
        toast.success("Employee archived successfully");
        navigate("/employee-list");
      } else {
        throw new Error(response.data.error || "Failed to archive employee");
      }
    } catch (error) {
      console.error("Error archiving employee:", error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to archive employee";
      toast.error(errorMessage);
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveEmployee = async () => {
    try {
      setArchiving(true);
      const response = await UserService.archiveUser(employee._id, false);
      if (response.data.success) {
        toast.success("Employee unarchived successfully");
        navigate("/employee-list");
      } else {
        throw new Error(response.data.error || "Failed to unarchive employee");
      }
    } catch (error) {
      console.error("Error unarchiving employee:", error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to unarchive employee";
      toast.error(errorMessage);
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-4 mt-5 pt-5">
        <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex flex-col items-center mt-5 pt-5">
        <h2 className="text-xl font-semibold text-gray-800">Employee not found</h2>
        <button 
          className="mt-3 px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
          style={{ backgroundColor: colors.highlights.callToAction }}
          onClick={handleClose}
        >
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h4 className="text-xl font-semibold" style={{ color: colors.primary.headingsBody }}>Employee Details</h4>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {!employee?.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
              style={{ backgroundColor: colors?.warning?.dark || '#f59e0b' }}
              onClick={handleArchiveEmployee}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <IoMdArchive className="h-4 w-4 mr-2" />
              )}
              <span className="text-sm">{archiving ? 'Archiving...' : 'Archive Employee'}</span>
            </button>
          )}
          {employee?.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
              style={{ backgroundColor: colors.success.dark }}
              onClick={handleUnarchiveEmployee}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <IoMdArchive className="h-4 w-4 mr-2" />
              )}
              <span className="text-sm">{archiving ? 'Unarchiving...' : 'Unarchive Employee'}</span>
            </button>
          )}
          {!employee?.isArchived && (
            <button
              className="px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
              style={{ backgroundColor: colors.highlights.callToAction }}
              onClick={() => navigate(`/edit-employee/${id}`)}
            >
              Edit Employee
            </button>
          )}
          <button
            className="px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
            style={{ backgroundColor: colors.darkAccents }}
            onClick={() => navigate("/employee-list")}
          >
            Back to List
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Name</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.name}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Email</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.email}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Phone</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.phone}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Experience</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.experience} Years
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Availability</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  employee.availability 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {employee.availability ? "Available" : "Not Available"}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Status</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.isArchived ? "Archived" : (employee.isActive ? "Active" : "Inactive")}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Skill Type</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200 flex flex-wrap gap-2">
                {employee.skills.map(skill => (
                  <div key={skill._id} className="text-sm p-1 rounded-md bg-green-100 text-green-800">
                    {skill.categoryType}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <h4 className="text-lg font-semibold mt-8 mb-4" style={{ color: colors.primary.headingsBody }}>Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Street</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.address.street}
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>City</label>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                {employee.address.city}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewEmployee;
